import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { voiceApi } from '../api/voiceApi';
import type {
  AutoCallRule,
  CreateAutoCallRuleInput,
  CreateVoiceTemplateInput,
  UpdateAutoCallRuleInput,
  UpdateVoiceTemplateInput,
  VoiceLibraryFilters,
  VoicePreviewInput,
} from '../types';

const VOICE_KEYS = {
  all: ['voice'] as const,
  voices: () => [...VOICE_KEYS.all, 'voices'] as const,
  models: () => [...VOICE_KEYS.all, 'models'] as const,
  library: (filters: VoiceLibraryFilters) => [...VOICE_KEYS.all, 'library', filters] as const,
  recommended: (orgId: string) => [...VOICE_KEYS.all, 'recommended', orgId] as const,
  templates: (orgId: string) => [...VOICE_KEYS.all, 'templates', orgId] as const,
  template: (id: string, orgId: string) => [...VOICE_KEYS.all, 'template', id, orgId] as const,
  autoCallRule: (orgId: string) => [...VOICE_KEYS.all, 'auto-call-rule', orgId] as const,
};

export function useElevenLabsVoices() {
  return useQuery({
    queryKey: VOICE_KEYS.voices(),
    queryFn: voiceApi.getVoices,
    staleTime: 1000 * 60 * 30,
  });
}

export function useElevenLabsModels() {
  return useQuery({
    queryKey: VOICE_KEYS.models(),
    queryFn: voiceApi.getModels,
    staleTime: 1000 * 60 * 30,
  });
}

export function useVoiceLibrary(filters: VoiceLibraryFilters, enabled = true) {
  return useQuery({
    queryKey: VOICE_KEYS.library(filters),
    queryFn: () => voiceApi.getVoiceLibrary(filters),
    staleTime: 1000 * 60 * 15,
    enabled,
  });
}

export function useRecommendedVoices(organizationId: string) {
  return useQuery({
    queryKey: VOICE_KEYS.recommended(organizationId),
    queryFn: () => voiceApi.getRecommendedVoices(organizationId),
    staleTime: 1000 * 60 * 30,
    enabled: !!organizationId,
  });
}

export function useVoicePreview() {
  return useMutation({
    mutationFn: (input: VoicePreviewInput) => voiceApi.generatePreview(input),
  });
}

export function useSaveVoicePreview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: VoicePreviewInput) => voiceApi.generateAndSavePreview(input),
    onSuccess: (_, variables) => {
      if (variables.organizationId) {
        queryClient.invalidateQueries({
          queryKey: VOICE_KEYS.templates(variables.organizationId),
        });
        if (variables.templateId) {
          queryClient.invalidateQueries({
            queryKey: VOICE_KEYS.template(variables.templateId, variables.organizationId),
          });
        }
      }
    },
  });
}

// A 403 here means the org's current plan doesn't include voice (PlanGuard on
// the backend), not a transient failure — retrying can't succeed, so we stop
// immediately instead of burning the default retry budget against an
// endpoint that will keep answering 403.
function isForbidden(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 403;
}

export function useVoiceTemplates(organizationId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: VOICE_KEYS.templates(organizationId),
    queryFn: () => voiceApi.getTemplates(organizationId),
    enabled: !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => (isForbidden(error) ? false : failureCount < 2),
  });
}

export function useVoiceTemplate(id: string, organizationId: string) {
  return useQuery({
    queryKey: VOICE_KEYS.template(id, organizationId),
    queryFn: () => voiceApi.getTemplate(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCreateVoiceTemplate() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: (input: CreateVoiceTemplateInput) => voiceApi.createTemplate(input),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'voice.template_created');
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.templates(variables.organizationId),
      });
    },
  });
}

export function useUpdateVoiceTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      organizationId,
      input,
    }: {
      id: string;
      organizationId: string;
      input: UpdateVoiceTemplateInput;
    }) => voiceApi.updateTemplate(id, organizationId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.template(variables.id, variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.templates(variables.organizationId),
      });
    },
  });
}

export function useDeleteVoiceTemplate() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      voiceApi.deleteTemplate(id, organizationId),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'voice.template_deleted');
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.templates(variables.organizationId),
      });
    },
  });
}

export function useDuplicateVoiceTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      organizationId,
      newName,
    }: {
      id: string;
      organizationId: string;
      newName: string;
    }) => voiceApi.duplicateTemplate(id, organizationId, newName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.templates(variables.organizationId),
      });
    },
  });
}

export function useAutoCallRule(organizationId: string) {
  return useQuery<AutoCallRule | null>({
    queryKey: VOICE_KEYS.autoCallRule(organizationId),
    queryFn: () => voiceApi.getAutoCallRule(organizationId),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCreateAutoCallRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAutoCallRuleInput) => voiceApi.createAutoCallRule(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.autoCallRule(variables.organizationId),
      });
    },
  });
}

export function useUpdateAutoCallRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      input,
    }: {
      organizationId: string;
      input: UpdateAutoCallRuleInput;
    }) => voiceApi.updateAutoCallRule(organizationId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.autoCallRule(variables.organizationId),
      });
    },
  });
}

export function useDeleteAutoCallRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organizationId: string) => voiceApi.deleteAutoCallRule(organizationId),
    onSuccess: (_, organizationId) => {
      queryClient.invalidateQueries({
        queryKey: VOICE_KEYS.autoCallRule(organizationId),
      });
    },
  });
}
